import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { buildAnnotationKey } from './buildAnnotationKey';
import {
    DashboardMetricAnnotationTypeResponse,
    QUERY_DASHBOARD_METRIC_ANNOTATIONS,
    QueryDashboardMetricAnnotationsResponse,
    QueryDashboardMetricAnnotationsVariables,
} from '../../graphql/queries/dashboard/queryDashboardMetricAnnotations';
import {
    CREATE_DASHBOARD_METRIC_ANNOTATION,
    CreateDashboardMetricAnnotationResponse,
    CreateDashboardMetricAnnotationVariables,
    DELETE_DASHBOARD_METRIC_ANNOTATION,
    DeleteDashboardMetricAnnotationResponse,
    DeleteDashboardMetricAnnotationVariables,
    UPDATE_DASHBOARD_METRIC_ANNOTATION,
    UpdateDashboardMetricAnnotationResponse,
    UpdateDashboardMetricAnnotationVariables,
} from '../../graphql/mutations/dashboard/dashboardMetricAnnotationMutations';

export interface DashboardMetricAnnotation {
    id: string;
    pageKey: string;
    dateKey: string;
    columnType: string;
    hubId: string;
    metricKey: string;
    dimensions: Record<string, string>;
    content: string;
    createdById: string;
    createdByName: string;
    updatedById?: string | null;
    updatedByName?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateDashboardAnnotationInput {
    dateKey: string;
    columnType: string;
    hubId: string;
    metricKey: string;
    dimensions: Record<string, string>;
    content: string;
}

export interface UpdateDashboardAnnotationInput {
    id: string;
    content: string;
}

export interface UseDashboardAnnotationsParams {
    pageKey: string;
    dateKeys: string[];
}

export interface UseDashboardAnnotationsResult {
    loading: boolean;
    annotationsByKey: Map<string, DashboardMetricAnnotation[]>;
    createAnnotation: (input: CreateDashboardAnnotationInput) => Promise<void>;
    updateAnnotation: (input: UpdateDashboardAnnotationInput) => Promise<void>;
    deleteAnnotation: (id: string) => Promise<void>;
    refetchAnnotations: () => Promise<void>;
}

function toAnnotation(a: DashboardMetricAnnotationTypeResponse): DashboardMetricAnnotation {
    let dimensions: Record<string, string> = {};
    try {
        dimensions = JSON.parse(a.dimensions) as Record<string, string>;
    } catch {
        // 解析失败时保持空对象，避免单个脏数据阻断整页渲染
    }
    return {
        id: a.id,
        pageKey: a.pageKey,
        dateKey: a.dateKey,
        columnType: a.columnType,
        hubId: a.hubId,
        metricKey: a.metricKey,
        dimensions,
        content: a.content,
        createdById: a.createdById,
        createdByName: a.createdByName,
        updatedById: a.updatedById,
        updatedByName: a.updatedByName,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
    };
}

/** 按 pageKey + dateKeys 拉取看板指标注释，并提供创建/更新能力；页面无关，签名维度由调用方直接传入 */
export function useDashboardAnnotations({
    pageKey,
    dateKeys,
}: UseDashboardAnnotationsParams): UseDashboardAnnotationsResult {
    const { data, loading, refetch } = useQuery<
        QueryDashboardMetricAnnotationsResponse,
        QueryDashboardMetricAnnotationsVariables
    >(QUERY_DASHBOARD_METRIC_ANNOTATIONS, {
        variables: { pageKey, dateKeys },
        skip: dateKeys.length === 0,
        fetchPolicy: 'network-only',
    });

    const [createMutation] = useMutation<
        CreateDashboardMetricAnnotationResponse,
        CreateDashboardMetricAnnotationVariables
    >(CREATE_DASHBOARD_METRIC_ANNOTATION);

    const [updateMutation] = useMutation<
        UpdateDashboardMetricAnnotationResponse,
        UpdateDashboardMetricAnnotationVariables
    >(UPDATE_DASHBOARD_METRIC_ANNOTATION);

    const [deleteMutation] = useMutation<
        DeleteDashboardMetricAnnotationResponse,
        DeleteDashboardMetricAnnotationVariables
    >(DELETE_DASHBOARD_METRIC_ANNOTATION);

    const annotationsByKey = useMemo(() => {
        const map = new Map<string, DashboardMetricAnnotation[]>();
        (data?.queryDashboardMetricAnnotations ?? []).forEach((raw) => {
            const a = toAnnotation(raw);
            const key = buildAnnotationKey(a.pageKey, a.dimensions, a.dateKey, a.columnType, a.hubId, a.metricKey);
            const list = map.get(key) ?? [];
            list.push(a);
            map.set(key, list);
        });
        map.forEach((list) => list.sort((x, y) => x.createdAt.localeCompare(y.createdAt)));
        return map;
    }, [data]);

    const createAnnotation = useCallback(
        async (input: CreateDashboardAnnotationInput) => {
            await createMutation({
                variables: {
                    input: {
                        pageKey,
                        dateKey: input.dateKey,
                        columnType: input.columnType,
                        hubId: input.hubId,
                        metricKey: input.metricKey,
                        dimensions: JSON.stringify(input.dimensions),
                        content: input.content,
                    },
                },
            });
            await refetch();
        },
        [pageKey, createMutation, refetch]
    );

    const updateAnnotation = useCallback(
        async (input: UpdateDashboardAnnotationInput) => {
            await updateMutation({ variables: { input } });
            await refetch();
        },
        [updateMutation, refetch]
    );

    const deleteAnnotation = useCallback(
        async (id: string) => {
            await deleteMutation({ variables: { id } });
            await refetch();
        },
        [deleteMutation, refetch]
    );

    return {
        loading,
        annotationsByKey,
        createAnnotation,
        updateAnnotation,
        deleteAnnotation,
        refetchAnnotations: async () => { await refetch(); },
    };
}
